import { Box, styled, alpha } from '@mui/material';
import { LayoutComponentNames } from '@diana/core';

export const StyledContainerComponentWrapper = styled(Box)<{
  hovered: boolean;
  active: boolean;
}>(({ hovered, active, theme }) => {
  return {
    height: '100%',
    minHeight: 40,
    position: 'relative',
    borderWidth: 1,
    borderStyle: 'solid',
    borderRadius: 8,
    transition: 'box-shadow 0.3s, border-color 0.3s',
    borderColor: active
      ? theme.palette.primary.main
      : hovered
      ? theme.palette.divider
      : 'transparent',
    cursor: active ? 'auto' : 'pointer',
    boxShadow: active
      ? `0 0 0 4px ${theme.palette.primary.focus}, 0 1px 2px 0 ${alpha(
          theme.palette.divider,
          0.05,
        )}`
      : 'none',
    '&, & > *': { transform: 'translate3d(0, 0, 0)' },
  };
});

export const StyledFieldComponentWrapper = styled(Box)<{
  active: boolean;
  hovered: boolean;
  invisible: boolean;
  canEdit: boolean;
}>(({ active, hovered, invisible, canEdit, theme }) => ({
  height: '100%',
  position: 'relative',
  borderRadius: theme.spacing(0.5),
  backgroundColor: !active && hovered ? theme.palette.action.hover : 'transparent',
  transition: 'background 0.3s ease-in-out, box-shadow 0.3s, border-color 0.3s',
  boxShadow: active
    ? `0 0 0 4px ${theme.palette.primary.focus}, 0 1px 2px 0 ${alpha(theme.palette.divider, 0.05)}`
    : 'none',
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: active ? theme.palette.primary.main : 'transparent',
  paddingRight: theme.spacing(0.5),
  paddingLeft: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
  cursor: canEdit && !active ? 'pointer' : 'auto',
  '& > *': { pointerEvents: canEdit && !active ? 'none' : 'auto' },

  '&, & > *': { transform: 'translate3d(0, 0, 0)' },

  '& .MuiFormControl-root': {
    opacity: invisible ? 0.6 : undefined,
  },

  // Since in builder view, all fields are read-only and sdk-mui package contains different styles
  // for read-only fields, we need to override them here
  '& .MuiInputBase-root': {
    '&.MuiInputBase-readOnly, &.Mui-disabled': {
      color: theme.palette.text.secondary,
      '&:hover': {
        ':before': {
          borderColor: `${theme.palette.border.disabled} !important`,
        },
      },
      '&:before': {
        borderColor: theme.palette.border.disabled,
      },
      '.MuiAutocomplete-tag:first-child': {
        marginLeft: '5px',
      },
      input: {
        '&.MuiInputBase-readOnly': {
          padding: '8px !important',
          color: theme.palette.text.secondary,
          '-webkit-text-fill-color': theme.palette.text.secondary,
        },
      },
      '&:has(input[value=""]):after': {
        display: 'none !important',
      },
    },
  },

  [`div[data-layout-component="${LayoutComponentNames.RICHTEXT_INPUT}"] &`]: {
    '.ck.ck-content.ck-editor__editable.ck-read-only': {
      borderColor: `${theme.palette.border.disabled} !important`,
      backgroundColor: 'transparent',
      color: theme.palette.text.secondary,
      '[data-placeholder]': {
        color: theme.palette.text.secondary,
      },
    },
  },

  [`div[data-layout-component="${LayoutComponentNames.STEP_SLIDER}"] &`]: {
    '.MuiBox-root': {
      color: theme.palette.text.secondary,
    },
  },

  '& .MuiFormControlLabel-label.Mui-disabled': { color: theme.palette.text.secondary },
}));

export const NodePortalWrapper = styled(Box)({
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  border: 0,
  whiteSpace: 'nowrap',
});
